import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent, 
  Paper, 
  CircularProgress, 
  Chip 
} from '@mui/material';
import { 
  History, 
  Person, 
  ArrowForward, 
  EventNote 
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getCandidateStatusHistory } from '../services/onboardingapi'; // Make sure this is exported in your API file

const StatusHistoryTimeline = ({ candidateId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!candidateId) return;
      try {
        const data = await getCandidateStatusHistory(candidateId);
        // Sort by date descending (newest on top)
        const sortedData = data.sort((a, b) => new Date(b.changeDate) - new Date(a.changeDate));
        setHistory(sortedData);
      } catch (err) {
        console.error("Failed to load history", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [candidateId]);

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    // Replace underscores with spaces and Title Case
    return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusColor = (status) => {
    const s = status || '';
    if (s.includes('DECLINED') || s.includes('REJECTED')) return 'error';
    if (s.includes('HIRED') || s.includes('ACCEPTED')) return 'success';
    if (s.includes('PENDING')) return 'warning';
    return 'primary';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>;

  if (history.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', bgcolor: 'rgba(249, 250, 251, 0.5)', borderRadius: 2 }}>
        <EventNote sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2">No status changes recorded yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper orientation="vertical">
        {history.map((record, index) => (
          <Step key={record.historyId || index} active={true}>
            <StepLabel
              icon={
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: index === 0 ? '#0EA5E9' : '#CBD5E1', // Highlight latest
                    boxShadow: index === 0 ? '0 0 0 3px rgba(14, 165, 233, 0.2)' : 'none'
                  }} 
                />
              }
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Typography variant="subtitle2" fontWeight="700" color="#1F2937">
                  {formatStatus(record.newStatus)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {format(new Date(record.changeDate), 'dd MMM yyyy, HH:mm')}
                </Typography>
              </Box>
            </StepLabel>
            
            <StepContent>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 3, mt: 1 }}>
                {/* Status Transition */}
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                    <Chip 
                        label={formatStatus(record.previousStatus)} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontSize: '0.75rem', height: '24px', borderColor: '#94A3B8', color: '#64748B' }}
                    />
                    <ArrowForward sx={{ fontSize: 16, color: '#94A3B8' }} />
                    <Chip 
                        label={formatStatus(record.newStatus)} 
                        size="small" 
                        color={getStatusColor(record.newStatus)}
                        sx={{ fontSize: '0.75rem', height: '24px', fontWeight: 600 }}
                    />
                </Box>

                {/* Changed By Info */}
                <Box display="flex" alignItems="center" gap={1}>
                   <Person sx={{ fontSize: 16, color: '#6B7280' }} />
                   <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                     Changed by <span style={{ fontWeight: 600, color: '#374151' }}>{record.changedByFullName || 'System'}</span>
                   </Typography>
                </Box>
              </Paper>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default StatusHistoryTimeline;